import * as React from 'react';
import { useState } from 'react';
import { YStack } from 'tamagui';

import { format, SHORT_READABLE_DATE_FORMAT } from '../../utils/dateUtils';
import { Button, ButtonSize, ButtonVariant } from '../buttons/Button';
import { RowList } from '../rows/RowList';
import { TextRow } from '../rows/TextRow';
import { Sheet } from '../sheet/Sheet';
import { LabelEmphasized } from '../text/Typography';
import { DateSelectionSheet } from './DateSelectionSheet';

interface DateRangeSelectionSheetProps {
    defaultToDate: Date;
    defaultFromDate: Date;
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (fromDate: Date, toDate: Date) => void;
    confirmButtonText?: string;
    minimumDate?: Date;
    maximumDate?: Date;
}

export const DateRangeSelectionSheet = ({
    defaultToDate,
    defaultFromDate,
    isOpen,
    onClose,
    onConfirm,
    confirmButtonText = 'Confirm',
    minimumDate,
    maximumDate,
}: DateRangeSelectionSheetProps) => {
    const [fromDate, setFromDate] = useState(defaultFromDate);
    const [toDate, setToDate] = useState(defaultToDate);

    const [isFromDateSheetOpen, setIsFromDateSheetOpen] = useState(false);
    const [isToDateSheetOpen, setIsToDateSheetOpen] = useState(false);

    const [hasFromDateSheetBeenOpened, setHasFromDateSheetBeenOpened] =
        useState(false);
    const [hasToDateSheetBeenOpened, setHasToDateSheetBeenOpened] =
        useState(false);

    const [error, setError] = useState<string | null>(null);

    const onCloseSheet = () => {
        setError(null);
        onClose();
    };

    const onClear = () => {
        setFromDate(defaultFromDate);
        setToDate(defaultToDate);
        setHasFromDateSheetBeenOpened(false);
        setHasToDateSheetBeenOpened(false);
    };

    const isValid = (): boolean => {
        if (fromDate > toDate) {
            setError('To date must be after from date');
            return false;
        }
        setError(null);
        return true;
    };

    const getSubtitleText = (
        selectedDate: Date,
        hasDateSheetBeenOpened: boolean,
    ): string =>
        hasDateSheetBeenOpened && selectedDate != null ? '' : 'Select date';

    const getSelectedValueText = (
        selectedDate: Date,
        hasDateSheetBeenOpened: boolean,
    ): string =>
        hasDateSheetBeenOpened
            ? format(selectedDate, SHORT_READABLE_DATE_FORMAT)
            : '';

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onCloseSheet}
            title="Select a date range"
            showCloseButton
            forceKeyboardCompatibleBottomSheet={true} // This is needed for cross-platform compatibility with layering of sheets/modals
        >
            <YStack gap={16}>
                <RowList>
                    <TextRow
                        title="Start"
                        subtitle={getSubtitleText(
                            fromDate,
                            hasFromDateSheetBeenOpened,
                        )}
                        secondaryText={getSelectedValueText(
                            fromDate,
                            hasFromDateSheetBeenOpened,
                        )}
                        onPress={() => {
                            setIsFromDateSheetOpen(true);
                        }}
                    />
                </RowList>
                <RowList>
                    <TextRow
                        title="End"
                        subtitle={getSubtitleText(
                            toDate,
                            hasToDateSheetBeenOpened,
                        )}
                        secondaryText={getSelectedValueText(
                            toDate,
                            hasToDateSheetBeenOpened,
                        )}
                        onPress={() => {
                            setIsToDateSheetOpen(true);
                        }}
                    />
                </RowList>
                {error != null && (
                    <LabelEmphasized textAlign="center" color="$red1100">
                        {error}
                    </LabelEmphasized>
                )}
                <Button
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.LARGE}
                    text="Clear dates"
                    onPress={onClear}
                />
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text={confirmButtonText}
                    disabled={error != null}
                    onPress={() => {
                        if (isValid()) {
                            onConfirm(fromDate, toDate);
                            onCloseSheet();
                        }
                    }}
                />
            </YStack>
            <DateSelectionSheet
                title="Start date"
                isOpen={isFromDateSheetOpen}
                onClose={() => {
                    setHasFromDateSheetBeenOpened(true);
                    setIsFromDateSheetOpen(false);
                    if (fromDate != null && !hasToDateSheetBeenOpened) {
                        setIsToDateSheetOpen(true);
                    }
                }}
                defaultValue={fromDate}
                onConfirm={setFromDate}
                minimumDate={minimumDate}
                maximumDate={maximumDate}
                isInsideModal={true}
            />
            <DateSelectionSheet
                title="End date"
                isOpen={isToDateSheetOpen}
                onClose={() => {
                    setHasToDateSheetBeenOpened(true);
                    setIsToDateSheetOpen(false);
                }}
                defaultValue={toDate}
                onConfirm={setToDate}
                minimumDate={minimumDate}
                maximumDate={maximumDate}
                isInsideModal={true}
            />
        </Sheet>
    );
};
